import { EVENT_SOURCE, SEARCH_DIALOG_EVENT_SOURCE } from '../../constants/analytics';
import type { SearchStoreExperience } from '../../constants/search';
import { SearchExperience } from '../../constants/search-experience';
import type { FireAnalyticsEvent } from '../../types';

type OnSmartLinkUsageAndTopAppsFetchSuccessArgs = {
	cloudId: string;
	experience: SearchStoreExperience;
	/**
	 * Tenant-wide percentage of end users who have rendered smartlinks.
	 * Optional because, when debugging via `debug_social_proof_percentage_trait_fetch`,
	 * we fire this event even when the trait resolves to empty/missing data — in
	 * which case there is no percentage to report.
	 */
	percentage?: number;
	/**
	 * Whether the fetch actually resolved with a parseable trait payload.
	 * `false` indicates the fetch succeeded but returned no usable data (the
	 * silent case we are trying to observe in prod).
	 */
	dataPresent?: boolean;
};

// Fires when the Smart Link usage and top apps trait is successfully fetched from the TAP delivery API
export const onSmartLinkUsageAndTopAppsFetchSuccess = ({
	cloudId,
	experience,
	percentage,
	dataPresent,
}: OnSmartLinkUsageAndTopAppsFetchSuccessArgs): FireAnalyticsEvent => {
	return {
		action: 'taskSuccess',
		actionSubject: 'smartLinkUsageAndTopAppsFetch',
		eventType: 'operational',
		source: experience === SearchExperience.QUICK_FIND ? SEARCH_DIALOG_EVENT_SOURCE : EVENT_SOURCE,
		attributes: {
			additionalAttributes: {
				cloudId,
				percentage,
				dataPresent,
			},
		},
	};
};

type OnSmartLinkUsageAndTopAppsFetchFailArgs = {
	cloudId: string;
	experience: SearchStoreExperience;
	reason: string;
};

// Fires when the Smart Link usage and top apps trait fetch from the TAP delivery API fails
export const onSmartLinkUsageAndTopAppsFetchFail = ({
	cloudId,
	experience,
	reason,
}: OnSmartLinkUsageAndTopAppsFetchFailArgs): FireAnalyticsEvent => {
	return {
		action: 'taskFail',
		actionSubject: 'smartLinkUsageAndTopAppsFetch',
		eventType: 'operational',
		source: experience === SearchExperience.QUICK_FIND ? SEARCH_DIALOG_EVENT_SOURCE : EVENT_SOURCE,
		attributes: {
			additionalAttributes: {
				cloudId,
				reason,
			},
		},
	};
};
